import FullCalendar from '@fullcalendar/react' // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid' // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import enGbLocale from '@fullcalendar/core/locales/en-gb'
import {DateSelectArg, EventApi, EventClickArg, EventContentArg, EventSourceFuncArg} from '@fullcalendar/core'
import {useEffect, useRef, useState} from 'react'
import {
    CalendarEventCreationParameters,
    CalendarEventType,
    Constraint,
    DeviceCreateParameters,
    EventScheduleClient,
    EventType,
    EventWithDetails,
    MfrrCreateParameters, PreferenceCreateParameters,
    UnavailabilityCreateParameters,
    WithEventId
} from '../../client/EventScheduleClient'
import {
    CalendarModalEvent,
    CalendarModalExistingEvent,
    CancelEventModal,
    CreateEventModal,
    EditEventModal
} from './CalendarEventModals'
import {Building} from '../BuildingStatus'
import {DateTime} from '../../time/DateTime'
import {DateRange} from "../../client/DataClient";
import {useDateTimeContext} from "../../DateTimeContext";
import {EventStatus} from "../../model/EventStatus";
import {useInterval} from "../../customHooks/useInterval";
import {Result} from "../../client/Result";
import {useEntitlements} from '../../EntitlementProvider'
import {Permission} from '../../Permission'
import {NotificationClient} from '../../client/NotificationsClient'
import './calendar.scss'
import luxonPlugin from '@fullcalendar/luxon3'
import {CircleAlertIcon} from '../icons/Icons'
import {useDebounceCallback} from '../../customHooks/useDebounce'
import {Timezone} from "../../time/Timezone";
import {BuildingClient, ControlStrategy} from "../../client/BuildingClient";

export interface CalendarProps {
    buildings: Building[]
    selectedBuildings: Building[],
    eventClient: EventScheduleClient,
    buildingClient: BuildingClient,
    notificationClient: NotificationClient,
    eventTypes: EventType[]
    refreshTime: number | null
    initialEvents?: CalendarEvent[],
    selectedTimeZone: Timezone
}

interface CalendarEventExtendedProps {
    type: EventType
    eventId: string
    eventStatus: EventStatus
    shouldBeWorried: boolean,
    parameters: CalendarEventCreationParameters
}

interface CalendarEvent {
    extendedProps: CalendarEventExtendedProps
    title: string
    start: string
    end: string
}

export function getBuildingId(calendarEventType: CalendarEventType, parameters: CalendarEventCreationParameters): number | undefined {
    switch (calendarEventType) {
        case 'device': return (parameters as DeviceCreateParameters).buildingId
        case 'unavailability': return (parameters as UnavailabilityCreateParameters).buildingId
        case 'preference': return (parameters as PreferenceCreateParameters).buildingId
        case 'mfrr_bidding_window': {
            const buildings = (parameters as MfrrCreateParameters)?.buildings;
            //todo: allow multi
            return buildings?.length > 0 ? buildings[0].id : undefined
        }
    }
}

export function getControlStrategyId(calendarEventType: CalendarEventType, parameters: CalendarEventCreationParameters): number | undefined {
    switch (calendarEventType) {
        case 'device': return (parameters as DeviceCreateParameters).controlStrategyId
        default: return undefined
    }
}

export const Calendar = ({
                             buildings,
                             eventClient,
                             buildingClient,
                             notificationClient,
                             eventTypes,
                             selectedBuildings = [],
                             refreshTime,
                             initialEvents = [],
                             selectedTimeZone
                         }: CalendarProps) => {
    const calendarRef = useRef<FullCalendar>(null)
    const now: () => DateTime = useDateTimeContext()

    const entitlements = useEntitlements()

    const [events, setEvents] = useState<CalendarEvent[]>(initialEvents)
    const [showModal, setShowModal] = useState<boolean>(false)
    const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
    const [updateEvent, setUpdateEvent] = useState<CalendarModalExistingEvent>()
    const [newEventDates, setNewEventDates] = useState<DateRange>()
    const [calendarInfo, setCalendarInfo] = useState<EventSourceFuncArg>()
    const [successfulEvents, setSuccessfulEvents] = useState<Map<string, boolean>>(new Map())

    const allowModal = eventTypes.length > 0

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                const calendarApi = calendarRef.current?.getApi();
                if (calendarApi) {
                    cleanModalState()
                    calendarApi.unselect()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }

    }, [])

    async function handleUnderPerformingEvents(start: Date, end: Date) {
        const results = await eventClient.getEventPerformance(DateTime.fromJSDate(start), DateTime.fromJSDate(end).plus({days: 1}))

        results.fold(eventPerformance =>
                setSuccessfulEvents(new Map(eventPerformance.map(f => [f.eventId.toString(), f.shouldThePerformanceWorryAPerson]))),
            () => undefined
        )

    }

    useEffect(() => {
        if (calendarInfo) {
            handleUnderPerformingEvents(calendarInfo.start, calendarInfo.end).then(() => {
                return
            })
        }
        refreshEvents().then(() => {
            return
        })
    }, [calendarInfo?.start, calendarInfo?.end, entitlements])

    useEffect(() => {
        refreshEvents().then(() => {
            return
        })
    }, [selectedBuildings])


    useInterval(() => {
        refreshEvents()
    }, refreshTime)

    async function retrieveCalendarEventDetails(start: DateTime, end: DateTime): Promise<EventWithDetails[]> {
        const buildingIds = selectedBuildings.length == 0 ? undefined : selectedBuildings.map(b => b.id)
        return (await eventClient.getEvents(start, end, buildingIds))
    }

    function toTitle(type: EventType, title: string, parameters: CalendarEventCreationParameters): string {
        switch (type.type) {
            case "preference":
                return buildings.find(b => b.id === (parameters as PreferenceCreateParameters).buildingId)?.name ?? '...'
            case 'unavailability':
                return buildings.find(b => b.id === (parameters as UnavailabilityCreateParameters).buildingId)?.name ?? '...'
            case 'device':
                // TODO: replace Test event logic with device event of subtype 'test event'
                return (title === 'Test event' ? 'Test event for ' : '') +
                    buildings.find(b => b.id === (parameters as DeviceCreateParameters).buildingId)?.name ?? '...'
            case 'mfrr_bidding_window': {
                const buildingNames: string[] = (parameters as MfrrCreateParameters).buildings
                    .flatMap(eventBuilding => {
                        const buildingName: string | undefined = buildings.find(b => b.id === eventBuilding.id)?.name
                        return buildingName ? [buildingName] : []
                    })

                return buildingNames.join(', ')
            }
        }
    }

    function isManual(type: EventType, parameters: CalendarEventCreationParameters):boolean {
        switch (type.type) {
            case 'preference':
                return buildings.find(b => b.id === (parameters as PreferenceCreateParameters).buildingId)?.controlEmails !== undefined
            case 'unavailability':
                return buildings.find(b => b.id === (parameters as UnavailabilityCreateParameters).buildingId)?.controlEmails !== undefined
            case 'device': {
                const controlEmail = buildings.find(b => b.id === (parameters as DeviceCreateParameters).buildingId)?.controlEmails;
                return controlEmail !== undefined
            }
            case 'mfrr_bidding_window': {
                const anyBuildingManual = (parameters as MfrrCreateParameters).buildings
                    .find(eventBuilding => {
                        return buildings.find(b => b.id === eventBuilding.id)?.controlEmails
                    })

                return anyBuildingManual !== undefined
            }
        }
    }


    function toCalendarEvent(e: EventWithDetails) {
        const extendedProps = (type: EventType, eventId: string, eventStatus: EventStatus, parameters: CalendarEventCreationParameters): CalendarEventExtendedProps => ({
            type,
            eventId,
            eventStatus,
            shouldBeWorried: !!successfulEvents.get(eventId),
            parameters
        })
        return ({
            extendedProps: extendedProps(e.eventType, e.id, e.eventStatus, e.parameters),
            id: e.id,
            start: e.start.toISO(),
            end: e.actualEndTime().toISO(),
            title: toTitle(e.eventType, e.title, e.parameters),
            className: `event-${toCSSStatus(e.eventStatus, e.eventType.type)} ${isManual(e.eventType, e.parameters) ? 'event-manual' : ''}`
        })
    }

    async function refreshEvents(): Promise<void> {
        async function extracted(startDate: string, endDate: string) {
            const start = DateTime.fromISO(startDate);
            const end = DateTime.fromISO(endDate);

            return (await retrieveCalendarEventDetails(start, end)).map(toCalendarEvent)
        }

        if (calendarInfo) {
            const refreshedCalendarEvents = await extracted(calendarInfo.startStr, calendarInfo.endStr);
            setEvents(refreshedCalendarEvents)
        }
    }

    async function handleCalendarModalEventCreation(event: CalendarModalEvent): Promise<Result<null>> {

        const creationResult = await eventClient.createCalendarEvent({
            start: event.startTime.toISO(),
            end: event.endTime.toISO(),
            calendarEventType: event.type.type,
            parameters: event.parameters
        })

        await refreshEvents()

        return creationResult
    }

    async function handleCalendarModalEventUpdate(event: CalendarModalEvent & WithEventId): Promise<Result<null>> {
        const result = await eventClient.updateEvent({
            eventId: event.eventId,
            start: event.startTime,
            end: event.endTime,
            parameters: event.parameters
        })

        await refreshEvents()

        return result
    }

    async function retrieveValidationInfo(event: CalendarModalEvent | (CalendarModalEvent & WithEventId)): Promise<Constraint | null> {
        if (event.type.type !== 'device') {
            return Promise.resolve(null)
        }

        const parameters = event.parameters as DeviceCreateParameters;
        return eventClient.retrieveValidationInfo({
            eventId: event['eventId'],
            type: event.type,
            startTime: event.startTime,
            endTime: event.endTime,
            buildingId: parameters.buildingId,
            controlStrategyId: parameters.controlStrategyId
        });
    }

    async function retrieveControlStrategies(buildingId?: number): Promise<ControlStrategy[]> {
        if (!buildingId) {
            return Promise.resolve([])
        }
        return buildingClient.retrieveBuildingStrategies(buildingId).then(value => value.unwrap([]));
    }

    async function handleCalendarModalEventCancellation(event: WithEventId): Promise<Result<null>> {
        const cancellationResult: Result<null> = await eventClient.deleteEvent(event)
        await refreshEvents()
        return cancellationResult
    }

    function cleanModalState() {
        setUpdateEvent(undefined)
        setNewEventDates(undefined)
        setShowModal(false)
        setShowCancelModal(false)
    }

    function handleDateSelect(selectInfo: DateSelectArg) {
        cleanModalState()
        setNewEventDates(new DateRange(DateTime.fromJSDate(selectInfo.start, {zone: selectedTimeZone.name}), DateTime.fromJSDate(selectInfo.end, {zone: selectedTimeZone.name})))
        setShowModal(true)
    }

    function allowSelect(selectInfo: DateSelectArg) {
        cleanModalState()
        const start = DateTime.fromJSDate(selectInfo.start)
        return start.toUnixInteger() > now().toUnixInteger()

    }

    function fullcalendarEventToCalendarModalExistingEvent(event: EventApi): CalendarModalExistingEvent {
        const {
            eventId,
            type,
            eventStatus,
            parameters
        }: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps
        return {
            startTime: DateTime.fromJSDate(event.start!, {zone: selectedTimeZone.name}),
            endTime: DateTime.fromJSDate(event.end!, { zone: selectedTimeZone.name }),
            eventId: eventId,
            type: type,
            title: event.title,
            eventStatus: eventStatus,
            parameters
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
    function handleEventClick(clickInfo: EventClickArg) {
        if (allowModal) {
            cleanModalState()
            const event: EventApi = clickInfo.event;
            const type = (event.extendedProps as CalendarEventExtendedProps).type

            if (type.type == 'unavailability' && entitlements.has(Permission.WRITE_UNAVAILABILTY)) {
                setUpdateEvent(fullcalendarEventToCalendarModalExistingEvent(event))
                setShowModal(true)
            } else if (type.type != 'unavailability' && entitlements.has(Permission.WRITE_EVENT)) {
                setUpdateEvent(fullcalendarEventToCalendarModalExistingEvent(event))
                setShowModal(true)
            }
            else if (type.type != 'unavailability' && entitlements.has(Permission.WRITE_UNAVAILABILTY)) {
                setUpdateEvent(fullcalendarEventToCalendarModalExistingEvent(event))
                setShowCancelModal(true)
            }
        }
    }

    function renderEventContent(eventContent: EventContentArg) {
        return eventContent.event.end && eventContent.event.start && ((eventContent.event.end.getTime() - eventContent.event.start.getTime()) > 45 * 60 * 1000) ?
            (
                <div data-testid={`event-${eventContent.event.id}`} className={'long-event'}>
                    <b>{eventContent.event.title}</b><span> </span>
                    {eventContent.event.extendedProps.type?.displayName}
                    {eventContent.event.extendedProps.shouldBeWorried ? <CircleAlertIcon/> : null}
                    <br></br>
                    {eventContent.timeText}
                </div>
            ) : <div data-testid={`event-${eventContent.event.id}`} className={'short-event'}>
                {eventContent.event.extendedProps.shouldBeWorried ? <CircleAlertIcon/> : null}
                <span> </span>
                <b>{eventContent.event.title}</b>&nbsp;
                {eventContent.event.extendedProps.type?.displayName}
            </div>
    }

    async function handleEventCancellationRequest() {
        await notificationClient.requestCancellation({eventId: updateEvent!.eventId})
    }

    type EventStatusCss = 'waiting' | 'expected' | 'preference' | 'unavailable' | 'modified'| 'error'
    function toCSSStatus(eventStatus: EventStatus, calendarEventType: CalendarEventType): EventStatusCss  {
        if (eventStatus.createdAwaitingSchedulerToSendToDevice() ||
            eventStatus.updatedNeverSentToDevice() ||
            eventStatus.updatedAwaitingSchedulerToSendToDevice() ||
            eventStatus.cancelledBeforeTheEventAwaitingRemovalFromDevice() ||
            eventStatus.cancelledDuringTheEventAwaitingRemovalFromDevice()
        ) return 'waiting'
        else if (calendarEventType == 'preference') return 'preference'
        else if (eventStatus.createdAndSuccessfullySentToDevice()) return 'expected'
        else if (eventStatus.cancelledDuringEventAndSuccessfullyRemovedFromDevice()) return 'modified'
        else if (eventStatus.unavailability()) return 'unavailable'
        else return 'error'
    }

    return <>
        <FullCalendar
            ref={calendarRef}
            key={selectedTimeZone.name}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin]}
            headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'timeGridWeek timeGridDay'
            }}
            initialView='timeGridWeek'
            firstDay={1}
            initialDate={now().toISO()}
            datesSet={useDebounceCallback(setCalendarInfo, 300)}
            editable={false}
            selectable={allowModal}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            allDaySlot={false}
            events={events}
            selectAllow={allowSelect}
            select={handleDateSelect}
            eventContent={renderEventContent} // custom render function
            eventClick={handleEventClick}
            timeZone={selectedTimeZone.name}
            height='auto'
            locale={enGbLocale}
        />
        {allowModal && showCancelModal && updateEvent &&
            <CancelEventModal
                show={true}
                setShow={setShowCancelModal}
                eventStart={updateEvent.startTime}
                eventEnd={updateEvent.endTime}
                building={buildings.find(b => b.id == getBuildingId(updateEvent.type.type, updateEvent.parameters))!}
                handleEventCancellationRequest={handleEventCancellationRequest}
            />
        }
        {allowModal && showModal && updateEvent &&
            <EditEventModal eventTypes={eventTypes}
                            eventUpdate={handleCalendarModalEventUpdate}
                            retrieveValidationInfo={retrieveValidationInfo}
                            retrieveControlStrategies={retrieveControlStrategies}
                            eventCancellation={handleCalendarModalEventCancellation}
                            buildings={buildings}
                            initialShow={showModal}
                            initialEvent={updateEvent}/>}
        {allowModal && showModal && newEventDates?.start && newEventDates.end &&
            <CreateEventModal buildings={buildings}
                              eventCreation={handleCalendarModalEventCreation}
                              eventTypes={eventTypes}
                              initialStart={newEventDates.start}
                              initialEnd={newEventDates.end}
                              initialShow={showModal}
                              retrieveValidationInfo={retrieveValidationInfo}
                              retrieveControlStrategies={retrieveControlStrategies}
            />}
    </>
}
